import Link from 'next/link';
import * as React from 'react';
import { useIntl } from 'react-intl';
import BooksNotes from '../../../../molecules/merchant/resources/book/BooksNotes';
import { BookingPageScheme } from '../../../../domain/entities/BookingPage';
import {
  MerchantBookingPageApiResponse,
  MerchantBookingPhotoGalleryImage,
  MerchantBookingStatus,
} from '../../../../interfaces/merchants/bookings';
import { MerchantBookingCourse } from '../../../../interfaces/merchants/bookings/courses';
import { MerchantBookingStaffApi } from '../../../../interfaces/merchants/bookings/staff';
import * as IBooks from '../../../../interfaces/merchants/resources/books';

import { MerchantReview } from '../../../../interfaces/merchants/reviews';
import Lightbox, { LightboxImage } from '../../../../components/Lightbox';
import GoogleMap from '../../../../molecules/common/GoogleMap';
import ReviewList from '../../../../molecules/merchant/reviews/ReviewList';
import Heading from '../../../../atoms/Heading';
import BaseTemplate from '../../../../organisms/merchants/BaseTemplate';
import DescriptionPanel from '../../../../molecules/merchant/DescriptionPanel';
import { useResourceNotes } from '../../../../helpers/merchants/resources/helper';
import MerchantProfile from '../../../../molecules/merchant/MerchantProfile';
import MerchantCourseDetailsAppointment from './components/Appointment';

import css from './MerchantBookingServiceShowPage.module.scss';
import MerchantCourseDetailsHeader from './components/MerchantCourseDetailsHeader';
import MerchantCourseDetailsHeaderImage from './components/MerchantCourseDetailsHeaderImage';
import MerchantCourseDetailsReservationButton from '../../../../molecules/merchant/resources/book/MerchantCourseDetailsReservationButton';

import { MerchantDetail } from '../../../../interfaces/merchants';
import MerchantCourseDetailsStaffList from '../../../../molecules/merchant/resources/book/MerchantCourseDetailsStaffList';
import { RSButtonLink } from '../../../../reservation-page/RSButtonLink';
import KantanIcon from '../../../../reservation-page/icons/kantan-icon.svg';
import MerchantStickyFooter from '../../../../organisms/merchants/MerchantStickyFooter';
import { useUserMe } from '../../../../hooks/api/me';
import { RSKantanIconButtonAnchor } from '../../../../reservation-page/RSKantanIconButtonAnchor';
import { useRouter } from 'next/router';
import { RSButtonAnchor } from 'src/reservation-page/RSButtonAnchor';
import {
  EventSchemeResource,
  CourseSchemeResource,
} from 'src/interfaces/reservation_flow';
import { buildSiteUrl, buildUrl } from 'src/utils/request-url';

const MAX_REVIEWS = 3;

export const mapPhotoGalleryImageToLightbox = (
  images: MerchantBookingPhotoGalleryImage[],
): LightboxImage[] => {
  const lightboxImages = images
    .map((image, i) => ({
      thumbUrl: findThumb(image),
      mainUrl: findImage(image),
      id: i,
    }))
    .filter((image) => image.mainUrl !== '' && image.thumbUrl !== '');

  return lightboxImages;
};

export interface PageProps {
  bookingData: MerchantBookingPageApiResponse;
  isBookable: boolean;
  reviews: MerchantReview[];
  settings: IBooks.SettingCompletion;
  merchantDetail: MerchantDetail;
  courses: MerchantBookingCourse[];
  staff: MerchantBookingStaffApi[];
  isCoubicApp: boolean;
  isV2Flow: boolean;
  resource: EventSchemeResource | CourseSchemeResource | undefined;
}

const MerchantbookingPageShowPage = ({
  bookingData,
  isBookable,
  settings,
  merchantDetail,
  courses,
  staff,
  reviews,
  isCoubicApp,
  isV2Flow,
  resource,
}: PageProps) => {
  const intl = useIntl();
  const { user } = useUserMe();
  const canShowReviews =
    merchantDetail.merchant_setting.is_customer_review_active &&
    merchantDetail.rate_count_starby &&
    reviews &&
    reviews.length > 0;

  const {
    cover_content,
    cover_contents_type,
    description,
    description_text,
    primary_image,
    is_json_description,
    require_customer_login,
    model_type,
    ...headerProps
  } = bookingData;
  const notes = useResourceNotes(settings);

  const isEvent = model_type === BookingPageScheme.Event;
  const isCourse =
    model_type === BookingPageScheme.Availability ||
    model_type === BookingPageScheme.Assign;

  const isFullyBooked =
    bookingData.status === MerchantBookingStatus.FullyBooked;
  // explore掲載専用マーチャントの場合、予約に関しての注意事項が紛らわしいので見せない。(予約導線はクービック内に無い為)
  const attentionForBookingVisible =
    !merchantDetail.merchant_setting.is_explore_only;

  const router = useRouter();

  const eventTypeReservationFlowQuery = React.useMemo(() => {
    const query = new URLSearchParams();
    if (!router.query.selected_slot) return '';
    query.set('timeSlotId', router.query.selected_slot as string);
    return `?${query.toString()}`;
  }, [router.query.selected_slot]);
  const eventTypeReservationFlowPath = buildSiteUrl(
    `/${bookingData.public_id}/book/event_type${eventTypeReservationFlowQuery}`,
  );

  const courseTypeReservationFlowQuery = React.useMemo(() => {
    const query = new URLSearchParams();
    if (!router.query.selected_date) return '';
    query.set('date', router.query.selected_date as string);
    return `?${query.toString()}`;
  }, [router.query.selected_date]);
  const courseTypeReservationFlowPath = buildSiteUrl(
    `/${bookingData.public_id}/book/course_type${courseTypeReservationFlowQuery}`,
  );

  const NewReservationFlowButton = React.useMemo(() => {
    if (resource && !resource.is_reservable)
      return (
        <div className="w-[200px]">
          <RSButtonAnchor
            isBlock={true}
            variant="outline"
            href={
              merchantDetail.merchant_setting.contact_url ||
              buildSiteUrl(`/contact?via=${bookingData.public_id}`)
            }
          >
            {intl.formatMessage({
              id: 'merchants.bookingPages.inquiry',
            })}
          </RSButtonAnchor>
        </div>
      );
    // 組織管理者・スタッフアクセスの場合はresourceが存在しないので、予約ボタンのみ表示する
    if (
      !resource ||
      (user && resource?.acceptable_customer_login_status !== 'guest')
    ) {
      return (
        <div className="w-[200px]">
          <RSButtonLink
            isBlock={true}
            href={
              isEvent
                ? eventTypeReservationFlowPath
                : courseTypeReservationFlowPath
            }
          >
            {intl.formatMessage({
              id: 'merchants.bookingPages.bookReservation',
            })}
          </RSButtonLink>
        </div>
      );
    }
    return (
      <div className="flex gap-1.5">
        {resource?.acceptable_customer_login_status === 'all' && (
          <div className="w-[200px]">
            <RSButtonLink
              isBlock={true}
              href={
                isEvent
                  ? eventTypeReservationFlowPath
                  : courseTypeReservationFlowPath
              }
            >
              {intl.formatMessage({
                id: 'merchants.bookingPages.bookGuestReservation',
              })}
            </RSButtonLink>
          </div>
        )}
        {resource?.acceptable_customer_login_status === 'guest' && (
          <div className="w-[200px]">
            <RSButtonLink
              isBlock={true}
              href={
                isEvent
                  ? eventTypeReservationFlowPath
                  : courseTypeReservationFlowPath
              }
            >
              {intl.formatMessage({
                id: 'merchants.bookingPages.bookReservation',
              })}
            </RSButtonLink>
          </div>
        )}
        {resource?.acceptable_customer_login_status !== 'guest' && (
          <div className="w-[200px]">
            <RSKantanIconButtonAnchor
              icon={<KantanIcon width="24" height="24" viewBox="0 0 24 24" />}
              isBlock={true}
              href={buildUrl(
                `/signin/user?return_to=${
                  isEvent
                    ? eventTypeReservationFlowPath
                    : courseTypeReservationFlowPath
                }`,
              )}
              variant="black"
            >
              {intl.formatMessage({
                id: 'merchants.bookingPages.bookReservationWithReservation',
              })}
            </RSKantanIconButtonAnchor>
          </div>
        )}
      </div>
    );
  }, [
    bookingData.public_id,
    courseTypeReservationFlowPath,
    eventTypeReservationFlowPath,
    intl,
    isEvent,
    merchantDetail.merchant_setting.contact_url,
    resource,
    user,
  ]);

  return (
    <>
      <BaseTemplate
        merchantDetail={merchantDetail}
        bookingPageIsBookable={isBookable}
        isCoubicApp={isCoubicApp}
      >
        <div className={css.MerchantBookingServiceShowPage}>
          <div className={css.MerchantBookingServiceShowPage__HeaderHeading}>
            <MerchantCourseDetailsHeader
              isRequireCustomerLogin={require_customer_login}
              isBookable={isBookable}
              merchantSetting={merchantDetail.merchant_setting}
              isFullyBooked={isFullyBooked}
              isV2Flow={isV2Flow}
              contact_url={merchantDetail.merchant_setting.contact_url}
              resource={resource}
              model_type={model_type as BookingPageScheme}
              newReservationFlowPath={
                isEvent
                  ? eventTypeReservationFlowPath
                  : courseTypeReservationFlowPath
              }
              {...headerProps}
            />
          </div>
          <div className={css.MerchantBookingServiceShowPage__HeroImage}>
            {cover_contents_type === 'upload_image' && primary_image && (
              <MerchantCourseDetailsHeaderImage
                name={headerProps.name}
                primary_image={primary_image}
              />
            )}
            {cover_contents_type === 'embed' &&
              cover_content &&
              cover_content.embed && (
                <div
                  className={css.MerchantBookingServiceShowPage__Embed}
                  dangerouslySetInnerHTML={{ __html: cover_content.embed }}
                />
              )}
          </div>
          {((is_json_description && description) || description_text) && (
            <div className={css.MerchantBookingServiceShowPage__Section}>
              <DescriptionPanel
                description={description}
                description_text={description_text}
                is_json_description={is_json_description}
              />
            </div>
          )}
          {isBookable && courses.length > 0 && (
            <div className={css.MerchantBookingServiceShowPage__Section}>
              <Heading className={css.MerchantBookingServiceShowPage__Heading}>
                {intl.formatMessage({
                  id: 'books.formsLabel.appointment',
                })}
              </Heading>
              <MerchantCourseDetailsAppointment
                resourceId={bookingData.public_id}
                courses={courses}
                staff={staff}
                resource={resource}
              />
            </div>
          )}
          {isEvent && staff.length > 0 && (
            <div className={css.MerchantBookingServiceShowPage__Section}>
              <Heading className={css.MerchantBookingServiceShowPage__Heading}>
                {intl.formatMessage({
                  id: 'books.formsLabel.staff',
                })}
              </Heading>
              <MerchantCourseDetailsStaffList staff={staff} />
            </div>
          )}
          {attentionForBookingVisible && (
            <div className={css.MerchantBookingServiceShowPage__Section}>
              <Heading className={css.MerchantBookingServiceShowPage__Heading}>
                {intl.formatMessage({
                  id: 'books.formsLabel.attentionForBooking',
                })}
              </Heading>
              <BooksNotes notes={notes} />

              {isV2Flow && (isEvent || isCourse) ? (
                <div className="legacyPc:flex hidden justify-end pt-2">
                  {NewReservationFlowButton}
                </div>
              ) : (
                <div className="mt-1 flex justify-end">
                  <div className="w-[200px]">
                    <MerchantCourseDetailsReservationButton
                      isBookable={isBookable}
                      contactUrl={merchantDetail.merchant_setting.contact_url}
                      {...bookingData}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {bookingData.map_widget && (
            <div className={css.MerchantBookingServiceShowPage__Section}>
              <Heading className={css.MerchantBookingServiceShowPage__Heading}>
                {intl.formatMessage({
                  id: 'merchants.details.access',
                })}
              </Heading>
              <GoogleMap
                displayName={merchantDetail.display_name}
                mapData={bookingData.map_widget}
              />
            </div>
          )}
          {bookingData.photo_gallery_images.length >= 1 && (
            <div className={css.MerchantBookingServiceShowPage__Section}>
              <Heading className={css.MerchantBookingServiceShowPage__Heading}>
                {intl.formatMessage({
                  id: 'merchants.bookingPages.photoGallery',
                })}
              </Heading>
              <Lightbox
                images={mapPhotoGalleryImageToLightbox(
                  bookingData.photo_gallery_images,
                )}
                alt={bookingData.name}
              />
            </div>
          )}
          {canShowReviews && (
            <div className={css.MerchantBookingServiceShowPage__Section}>
              <Heading className={css.MerchantBookingServiceShowPage__Heading}>
                {intl.formatMessage({ id: 'reviews.title' })}
              </Heading>
              <ReviewList
                reviews={reviews}
                merchantDetail={merchantDetail}
                max={MAX_REVIEWS}
                rate_count_starby={merchantDetail.rate_count_starby}
                showHeader={true}
              />
              {reviews.length > 0 && (
                <Link
                  href={buildSiteUrl(`/reviews`, 'next')}
                  prefetch={false}
                  className={css.MerchantBookingServiceShowPage__ReviewLink}
                >
                  {intl.formatMessage({
                    id: 'merchants.details.showMoreReviews',
                  })}
                </Link>
              )}
            </div>
          )}
          <div className={css.MerchantBookingServiceShowPage__Section}>
            <Heading className={css.MerchantBookingServiceShowPage__Heading}>
              {intl.formatMessage({ id: 'merchants.bookingPages.storeData' })}
            </Heading>
            <MerchantProfile merchantDetail={merchantDetail} />
            <div
              className={
                css.MerchantBookingServiceShowPage__BackToTopButtonSection
              }
            >
              <div className="mx-auto max-w-[370px]">
                <RSButtonAnchor variant="outline" isBlock href={buildSiteUrl()}>
                  {intl.formatMessage({
                    id: 'merchants.details.backToMerchantTop',
                  })}
                </RSButtonAnchor>
              </div>
            </div>
          </div>
        </div>
      </BaseTemplate>
      {isV2Flow && (isEvent || isCourse) && (
        <MerchantStickyFooter
          resource_public_id={bookingData.public_id}
          contactUrl={merchantDetail.merchant_setting.contact_url}
          resource={resource}
          newReservationFlowPath={
            isEvent
              ? eventTypeReservationFlowPath
              : courseTypeReservationFlowPath
          }
        />
      )}
    </>
  );
};

//  findThumb is helper
function findThumb(data: MerchantBookingPhotoGalleryImage): string {
  if (data.image.thumb && data.image.thumb.url) {
    return data.image.thumb.url || '';
  }
  if (data.image.square && data.image.square.url) {
    return data.image.square.url || '';
  }
  if (data.image.p800x600 && data.image.p800x600.url) {
    return data.image.p800x600.url || '';
  }
  if (data.image.p1280x1280 && data.image.p1280x1280.url) {
    return data.image.p1280x1280.url || '';
  }
  if (data.image && data.image.url) {
    return data.image.url || '';
  }
  return '';
}

//  findImage is helper
function findImage(image: MerchantBookingPhotoGalleryImage): string {
  if (image.image.p1280x1280 && image.image.p1280x1280.url) {
    return image.image.p1280x1280.url || '';
  }
  if (image.image.p800x600 && image.image.p800x600.url) {
    return image.image.p800x600.url || '';
  }
  if (image.image && image.image.url) {
    return image.image.url || '';
  }
  if (image.image.square && image.image.square.url) {
    return image.image.square.url || '';
  }
  if (image.image.thumb && image.image.thumb.url) {
    return image.image.thumb.url || '';
  }
  return '';
}

export default MerchantbookingPageShowPage;
